// Clients Management Component
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  InputAdornment,
  Alert,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  styled,
} from "@mui/material";
import {
  Add,
  Search,
  Edit,
  Delete,
  Phone,
  Email,
  MoreVert,
  Person,
  ViewList,
  ViewModule,
  Male,
  Female,
  Notifications,
  Schedule,
  HeadsetMic,
  AccessAlarm,
} from "@mui/icons-material";
import apiService from "../../services/api";
import { GENDER_OPTIONS_LIST } from "../../constants";
import PermissionWrapper from "../common/PermissionWrapper";
 
// Styled components for cleaner layout inspired by modern financial app design
const ClientCard = styled(Card)(({ theme }) => ({
  height: '280px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 12,
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  border: '1px solid rgba(0,0,0,0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  boxSizing: 'border-box',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
    border: '1px solid rgba(0,0,0,0.12)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: 'linear-gradient(90deg, #e91e63 0%, #9c27b0 100%)',
  }
}));

const ClientCardContent = styled(CardContent)({
  flexGrow: 1,
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  height: '100%',
  boxSizing: 'border-box'
});

const ClientHeader = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 8,
  paddingBottom: 8,
  borderBottom: '1px solid rgba(0,0,0,0.08)'
});

const ClientInfo = styled('div')({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  flex: 1
});

const ClientNameSection = styled('div')({
  minWidth: 0,
  flex: 1,
  marginLeft: 16
});

const ClientDetails = styled('div')({
  flexGrow: 1,
  minHeight: 40,
  maxHeight: 40,
  overflow: 'hidden',
  width: '100%',
  paddingBottom: 4
});

const InfoRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  padding: '4px 0',
  '& .icon': {
    fontSize: 16,
    marginRight: 8,
    color: '#6b7280',
    flexShrink: 0,
    width: 18,
    textAlign: 'center'
  }
});
 

const StatsSection = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 0,
  marginTop: 'auto',
  padding: '12px 16px',
  background: 'rgba(25, 118, 210, 0.04)',
  borderRadius: 8,
  border: '1px solid rgba(25, 118, 210, 0.08)',
  flexShrink: 0
});

const StatItem = styled('div')({
  textAlign: 'center',
  flex: 1
});
 

const ClientIdBadge = styled('div')({
  
  padding: '4px 0px', 
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#6b7280'
});

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  // Menu selection is separate from follow-ups selection to avoid clearing follow-ups state
  // when the actions menu closes.
  const [menuSelectedClient, setMenuSelectedClient] = useState(null);
  const [followupsClient, setFollowupsClient] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    address: "",
    date_of_birth: "",
    gender: "",
    notes: "",
  });
  const [mobileError, setMobileError] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "list" or "grid"
  const [followupsDialogOpen, setFollowupsDialogOpen] = useState(false);
  const [clientFollowups, setClientFollowups] = useState([]);
  const [followupsLoading, setFollowupsLoading] = useState(false);
  const [newFollowup, setNewFollowup] = useState({
    followup_date: new Date().toISOString().split('T')[0],
    followup_time: new Date().toTimeString().slice(0,5),
    next_followup_date: (() => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    })(),
    next_followup_time: new Date().toTimeString().slice(0,5),
    status: 'warm',
    notes: ''
  });
  const [creatingFollowup, setCreatingFollowup] = useState(false);


  useEffect(() => {
    fetchClients();
  }, []);

  // Debug follow-ups client changes
  useEffect(() => {
    console.log('followupsClient changed:', followupsClient);
  }, [followupsClient]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await apiService.get("/clients");
      console.log("Clients API response:", response);
      if (response.success) {
        // Handle the data structure: response.data.clients is the array
        setClients(response.data.clients || []);
      } else {
        setError("Failed to fetch clients");
      }
    } catch (err) {
      setError("Error fetching clients: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredClients = clients.filter(
    (client) => {
      const fullName = `${client.first_name || ''} ${client.last_name || ''}`.trim().toLowerCase();
      const email = (client.email || '').toLowerCase();
      const mobile = client.mobile || '';
      const searchLower = searchTerm.toLowerCase();
      
      return (
        fullName.includes(searchLower) ||
        email.includes(searchLower) ||
        mobile.includes(searchTerm)
      );
    }
  );

  const handleOpenDialog = (client = null) => {
    setDialogError(""); // Clear any previous dialog errors
    setMobileError(""); // Clear mobile errors
    if (client) {
      setEditingClient(client);
      setFormData({
        first_name: client.first_name,
        last_name: client.last_name,
        email: client.email,
        mobile: client.mobile,
        address: client.address || "",
        date_of_birth: client.date_of_birth || "",
        gender: client.gender || "",
        notes: client.notes || "",
      });
    } else {
      setEditingClient(null);
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        mobile: "",
        address: "",
        date_of_birth: "",
        gender: "",
        notes: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
    setDialogError(""); // Clear dialog errors when closing
    setMobileError(""); // Clear mobile errors when closing
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      address: "",
      date_of_birth: "",
      gender: "",
      notes: "",
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear mobile error when user starts typing in mobile field
    if (name === 'mobile' && mobileError) {
      setMobileError('');
    }
  };

  const handleSubmit = async () => {
    try {
      setDialogError(""); // Clear any previous dialog errors
      
      // Validate mobile number format (10 digits)
      const mobileRegex = /^\d{10}$/;
      if (!mobileRegex.test(formData.mobile)) {
        setMobileError('Mobile number must be exactly 10 digits');
        setDialogError('Please fix the mobile number format');
        return;
      }

      // Check for mobile number uniqueness
      try {
        const checkResponse = await apiService.get(`/clients/check-mobile/${formData.mobile}`);
        if (checkResponse.data && checkResponse.data.exists) {
          // If editing, allow the same mobile for the current client
          if (!editingClient || checkResponse.data.client_id !== editingClient.id) {
            setMobileError('This mobile number is already registered');
            setDialogError('This mobile number is already registered with another client');
            return;
          }
        }
      } catch (checkErr) {
        // If the check endpoint doesn't exist, we'll skip uniqueness validation
        console.warn('Mobile uniqueness check not available:', checkErr.message);
      }

      // Prepare form data, converting empty date to null
      const submitData = {
        ...formData,
        date_of_birth: formData.date_of_birth || null,
      };

      let response;
      if (editingClient) {
        response = await apiService.put(
          `/clients/${editingClient.id}`,
          submitData
        );
      } else {
        response = await apiService.post("/clients", submitData);
      }

      if (response.success) {
        fetchClients();
        handleCloseDialog();
        setSuccessMessage(
          editingClient
            ? "Client updated successfully!"
            : "Client created successfully!"
        );
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(""), 5000);
      } else {
        setDialogError(response.message || "Failed to save client");
      }
    } catch (err) {
      setDialogError("Error saving client: " + err.message);
    }
  };

  const handleMenuOpen = (event, client) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedClient(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSelectedClient(null);
  };

  const handleDeleteClient = async () => {
    if (menuSelectedClient) {
      try {
        const response = await apiService.delete(
          `/clients/${menuSelectedClient.id}`
        );
        if (response.success) {
          fetchClients();
          setSuccessMessage("Client deleted successfully!");
          // Clear success message after 5 seconds
          setTimeout(() => setSuccessMessage(""), 5000);
        } else {
          setError("Failed to delete client");
        }
      } catch (err) {
        setError("Error deleting client: " + err.message);
      }
    }
    handleMenuClose();
  };

  const handleViewFollowups = async (client) => {
    if (!client || !client.id) {
      const errorMsg = 'Client data not available. Please try opening the followups dialog again.';
      setError(errorMsg);
      setTimeout(() => setError(""), 5000);
      return;
    }

    try {
      setFollowupsLoading(true);
      console.log('Fetching followups for client:', client.id);
      
      // Store client data in a ref or use a more reliable method
      const clientData = {
        id: client.id,
        tenant_id: client.tenant_id || 1,
        first_name: client.first_name,
        last_name: client.last_name
      };
      
      // Set the selected client first and open dialog immediately
      setFollowupsClient(clientData);
      
      // Reset form state when opening dialog
      setError("");
      setSuccessMessage("");
      
      const url = `/followups/by-client?client_id=${client.id}`;
      console.log('API URL:', url);
      console.log('clientData API URL:', clientData);
      
      // Check authentication status
      // const sessionToken = sessionStorage.getItem('session_token');
      // const user = sessionStorage.getItem('user');
      // console.log('Session token exists:', !!sessionToken);
      // console.log('User data exists:', !!user);
      
      const response = await apiService.get(url);
      console.log('Followups API response:', response);
      setClientFollowups(response.data?.followups || []);


      setFollowupsDialogOpen(true);
      
    } catch (error) {
      console.error('Error fetching followups:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Network error: Unable to connect to server');
      } else if (error.message.includes('401')) {
        setError('Authentication error: Please log in again');
      } else if (error.message.includes('403')) {
        setError('Permission denied: You don\'t have access to this feature');
      } else {
        setError('Failed to fetch followups: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setFollowupsLoading(false);
    }
  };

  const handleCreateFollowup = async () => {
    console.log('handleCreateFollowup called');
    console.log('Current newFollowup state:', newFollowup);
    console.log('Selected followups client:', followupsClient);
    
    try {
      setCreatingFollowup(true);
      
      console.log('Validating followup data:', newFollowup);
      
      if (!newFollowup.followup_date) {
        const errorMsg = 'Follow-up date is required';
        console.log('Validation error:', errorMsg);
        setError(errorMsg);
        setTimeout(() => setError(""), 5000);
        return;
      }

      // Validate follow-up date is not greater than today (can't follow up on a future date)
      const followupDate = new Date(newFollowup.followup_date + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (followupDate > today) {
        const errorMsg = 'Follow-up date cannot be greater than today';
        console.log('Validation error:', errorMsg);
        setError(errorMsg);
        setTimeout(() => setError(""), 5000);
        return;
      }

      // Validate next follow-up date is not less than today (next follow-up must be today or future)
      if (newFollowup.next_followup_date) {
        const nextFollowupDate = new Date(newFollowup.next_followup_date + 'T00:00:00');
        if (nextFollowupDate < today) {
          const errorMsg = 'Next follow-up date cannot be less than today';
          console.log('Validation error:', errorMsg);
          setError(errorMsg);
          setTimeout(() => setError(""), 5000);
          return;
        }
      }

      // Get client data from followupsClient state
      if (!followupsClient || !followupsClient.id) {
        const errorMsg = 'Client data not available. Please try opening the followups dialog again.';
        console.log('Error:', errorMsg);
        setError(errorMsg);
        setTimeout(() => setError(""), 5000);
        return;
      }

      const clientId = followupsClient.id;
      const tenantId = followupsClient.tenant_id;

      const followupData = {
        client_id: clientId,
        tenant_id: tenantId,
        followup_date: newFollowup.followup_date,
        followup_time: newFollowup.followup_time,
        next_followup_date: newFollowup.next_followup_date,
        next_followup_time: newFollowup.next_followup_time,
        status: newFollowup.status,
        notes: newFollowup.notes
      };

      console.log('Creating followup with data:', followupData);
      console.log('Selected followups client:', followupsClient);

      const response = await apiService.post('/followups', followupData);
      console.log('API Response:', response);
      
      if (response.success) {
        // Reset form
        setNewFollowup({
          followup_date: '',
          followup_time: '',
          next_followup_date: '',
          next_followup_time: '',
          status: 'cold',
          notes: ''
        });
        
        // Refresh followups list - use the client data we already have
        const clientData = {
          id: clientId,
          tenant_id: tenantId,
          first_name: followupsClient?.first_name || 'Client',
          last_name: followupsClient?.last_name || ''
        };
        await handleViewFollowups(clientData);
        
        setSuccessMessage('Followup created successfully');
        setTimeout(() => setSuccessMessage(""), 5000);
      }
    } catch (error) {
      console.error('Error creating followup:', error);
              setError('Failed to create followup: ' + (error.message || 'Unknown error'));
        setTimeout(() => setError(""), 5000);
    } finally {
      setCreatingFollowup(false);
    }
  };
 

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${
      lastName?.charAt(0) || ""
    }`.toUpperCase();
  };

  const formatPhone = (phone) => {
    // Simple phone formatting
    return phone;
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getGenderColor = (gender) => {
    if (gender === 'male') return 'primary.main';
    if (gender === 'female') return 'secondary.main';
    return 'text.secondary';
  };

  const getGenderIcon = (gender) => {
    if (gender === 'male') return <Male sx={{ fontSize: 16, color: '#1976d2', marginRight: '4px' }} />;
    if (gender === 'female') return <Female sx={{ fontSize: 16, color: '#9c27b0', marginRight: '4px' }} />;
    return null;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>Loading clients...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          Clients
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          {/* View Toggle */}
          <Box display="flex" sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Button
              variant={viewMode === 'grid' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setViewMode('grid')}
              sx={{ borderRadius: '4px 0 0 4px', borderRight: 0 }}
            >
              <ViewModule sx={{ fontSize: 20 }} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setViewMode('list')}
              sx={{ borderRadius: '0 4px 4px 0' }}
            >
              <ViewList sx={{ fontSize: 20 }} />
            </Button>
          </Box>
           
          <PermissionWrapper permission="clients.add">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Add
            </Button>
          </PermissionWrapper>
          
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search clients by name, email, or phone..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Clients Display */}
      {viewMode === 'list' ? (
        // List View (Table)
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Last Visit</TableCell>
                <TableCell> Visits</TableCell> 
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box py={4}>
                      <Person sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        {searchTerm
                          ? "No clients found matching your search"
                          : "No clients yet"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {searchTerm
                          ? "Try adjusting your search terms"
                          : "Add your first client to get started"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => (
                  <TableRow key={client.id} hover>
                    <TableCell >
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 2, bgcolor: getGenderColor(client.gender) }}>
                          {getInitials(client.first_name, client.last_name)}
                        </Avatar>
                        <Box>
                          <Typography  variant="subtitle2" fontWeight="bold">
                            {client.first_name} {client.last_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Client ID: {client.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {client.email && (
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <Email
                              sx={{
                                fontSize: 16,
                                mr: 1,
                                color: "text.secondary",
                              }}
                            />
                            <Typography
                              variant="body2"
                              component="a"
                              href={`mailto:${client.email}`}
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                cursor: "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {client.email}
                            </Typography>
                          </Box>
                        )}
                        <Box display="flex" alignItems="center">
                          <Phone
                            sx={{ fontSize: 16, mr: 1, color: "text.secondary" }}
                          />
                          <Typography
                            variant="body2"
                            component="a"
                            href={`tel:${client.mobile}`}
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {formatPhone(client.mobile)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {client.last_visit
                          ? new Date(client.last_visit).toLocaleDateString()
                          : "Never"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {client.total_visits || 0}
                      </Typography>
                    </TableCell> 
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, client)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        // Grid View (Cards)
        <Grid container spacing={3}>
          {filteredClients.length === 0 ? (
            <Grid item xs={12}>
              <Card >
                <CardContent>
                  <Box py={4} textAlign="center">
                    <Person sx={{ fontSize: 64, color: "grey.400", mb: 2 }}  />
                    <Typography variant="h6" color="text.secondary">
                      {searchTerm
                        ? "No clients found matching your search"
                        : "No clients yet"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {searchTerm
                        ? "Try adjusting your search terms"
                        : "Add your first client to get started"}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            filteredClients.map((client) => (
              <Grid item xs={12} sm={6} md={3} lg={3} key={client.id} sx={{ display: 'flex' }}>
                <ClientCard sx={{ width: '100%' }}>
                  
                  <ClientCardContent>
                    {/* Header with Avatar and Name */}
                    <ClientHeader>
                      <ClientInfo>
                        <Avatar   onClick={() => {
            handleViewFollowups(client);
            //handleMenuClose();
          }}
                          sx={{ 
                            bgcolor: getGenderColor(client.gender),
                            width: 48,
                            height: 48,
                            fontSize: '1.2rem',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                            border: '2px solid white'
                          }}
                        >
                          {getInitials(client.first_name, client.last_name)}
                        </Avatar>
                        
                        <ClientNameSection>
                          <ClientIdBadge>Client ID: {client.id}</ClientIdBadge>
                          <Typography 
                            variant="h6" 
                            fontWeight="600" 
                            sx={{ 
                              fontSize: '1rem',
                              color: '#1a1a1a',
                              lineHeight: 1.3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {client.first_name} {client.last_name}
                          </Typography>
                          
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#6b7280',
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              marginTop: '2px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {client.gender && (
                              <span style={{ display: 'flex', alignItems: 'center', textTransform: 'capitalize', marginRight: 8 }}>
                                {getGenderIcon(client.gender)}
                                {client.gender}
                              </span>
                            )}
                            {client.date_of_birth && calculateAge(client.date_of_birth) && (
                              <span>{calculateAge(client.date_of_birth)} years</span>
                            )}
                          </Typography>
                        </ClientNameSection>
                      </ClientInfo>
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleMenuOpen(e, client)}
                        sx={{ 
                          color: '#6b7280',
                          '&:hover': { 
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            color: '#374151'
                          }
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </ClientHeader>

                    {/* Client Details */}
                    <ClientDetails>
                      {/* Contact Info - Mobile and Email in single row */}
                      {(client.mobile || client.email) && (
                        <InfoRow>
                          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                            {/* Phone on the left */}
                            {client.mobile && (
                              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <Phone className="icon" />
                                <Typography
                                  variant="body2"
                                  component="a"
                                  href={`tel:${client.mobile}`}
                                  sx={{
                                    textDecoration: "none",
                                    color: "#1976d2",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    "&:hover": {
                                      textDecoration: "underline",
                                      color: "#1565c0"
                                    },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {formatPhone(client.mobile)}
                                </Typography>
                              </div>
                            )}
                            
                            {/* Email on the right */}
                            {client.email && (
                              <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                                <Email className="icon" />
                                <Typography
                                  variant="body2"
                                  component="a"
                                  href={`mailto:${client.email}`}
                                  sx={{
                                    textDecoration: "none",
                                    color: "#1976d2",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                    fontSize: '0.85rem',
                                    "&:hover": { 
                                      textDecoration: "underline",
                                      color: "#1565c0"
                                    },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    opacity: 0.8
                                  }}
                                >
                                  {client.email}
                                </Typography>
                              </div>
                            )}
                          </div>
                        </InfoRow>
                      )}
 
                    </ClientDetails>

                    {/* Visit Stats */}
                    <StatsSection>
                      <StatItem>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            color: '#1976d2',
                            fontWeight: 700,
                            fontSize: '1.25rem',
                            lineHeight: 1
                          }}
                        >
                          {client.total_visits || 0}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#6b7280',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                           Visits
                        </Typography>
                      </StatItem>
                      <StatItem>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: '#374151',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            marginTop: '2px'
                          }}
                        >
                          {client.last_visit
                            ? new Date(client.last_visit).toLocaleDateString('en-IN', {
                                month: 'short',
                                day: 'numeric'
                              })
                            : "Never"}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#6b7280',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Last Visit
                        </Typography>
                      </StatItem>
                    </StatsSection>
 
                    {/* Last Follow-up Info */}
                    <Box sx={{ 
                      marginTop: '8px',
                      minHeight: '40px',
                      flexShrink: 0
                    }}>
                      {client.last_followup_notes && (
                        <Typography 
                          variant="body2"
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.4,
                            maxHeight: '2.8em'
                          }}
                        >
                          {client.last_followup_notes}
                        </Typography>
                      )}
                      {(client.last_followup_date) && (
                        <Typography style={{ fontSize: '0.8rem', marginTop: '4px' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <span>
                              <HeadsetMic sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                              {" "}
                              {client.last_followup_date
                                ? new Date(client.last_followup_date).toLocaleDateString('en-IN', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })
                                : ''}
                            </span>
                            <span>
                              <AccessAlarm sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                              {" "}
                              {client.next_followup_date
                                ? new Date(client.next_followup_date).toLocaleDateString('en-IN', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })
                                : ''}
                            </span>
                          </Box>
                        </Typography>
                      )}
                    </Box>
                  </ClientCardContent>
                </ClientCard>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <PermissionWrapper permission="clients.edit">
          <MenuItem
            onClick={() => {
              handleOpenDialog(menuSelectedClient);
              handleMenuClose();
            }}
          >
            <Edit sx={{ mr: 1 }} /> Edit
          </MenuItem>
        </PermissionWrapper>
        <MenuItem 
          onClick={() => {
            handleViewFollowups(menuSelectedClient);
            handleMenuClose();
          }}
        >
          <Notifications sx={{ mr: 1 }} />  Follow-ups
        </MenuItem>
        <MenuItem
          onClick={() => {
            const clientId = menuSelectedClient?.id;
            if (clientId) {
              navigate(`/dashboard/clients/${clientId}/history`);
            }
            handleMenuClose();
          }}
        >
          <Schedule sx={{ mr: 1 }} /> History
        </MenuItem>
        {/* <MenuItem 
          onClick={() => {
            window.location.href = `/dashboard/follow-ups?client_id=${selectedClient.id}`;
            handleMenuClose();
          }}
        >
          <Notifications sx={{ mr: 1 }} /> Create Follow-up
        </MenuItem> */}
        <PermissionWrapper permission="clients.delete">
          <MenuItem onClick={handleDeleteClient} sx={{ color: "error.main" }}>
            <Delete sx={{ mr: 1 }} /> Delete
          </MenuItem>
        </PermissionWrapper>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseDialog();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingClient ? "Edit Client" : "Add New Client"}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setDialogError("")}
            >
              {dialogError}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            {/* Row 1: First Name + Last Name */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                required
                autoComplete="off"
              />
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                autoComplete="off"
              />
            </Box>

            {/* Row 2: Email + Mobile */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="off"
              />
              <TextField
                fullWidth
                label="Mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                required
                placeholder="1234567890"
                helperText={mobileError || "Enter 10-digit mobile number"}
                error={!!mobileError}
                autoComplete="off"
                inputProps={{
                  maxLength: 10,
                  pattern: "[0-9]{10}"
                }}
                onInput={(e) => {
                  // Only allow digits
                  e.target.value = e.target.value.replace(/[^0-9]/g, '');
                }}
              />
            </Box>

            {/* Row 3: Date of Birth + Gender */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Date of Birth"
                name="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                autoComplete="off"
              />
              <TextField
                fullWidth
                label="Gender"
                name="gender"
                select
                value={formData.gender}
                onChange={handleInputChange}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                required
                autoComplete="off"
              >
                {GENDER_OPTIONS_LIST.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>
            </Box>

            {/* Row 4: Address (full width) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                multiline
                rows={2}
                value={formData.address}
                onChange={handleInputChange}
                autoComplete="off"
              />
            </Box>

            {/* Row 5: Notes (full width) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Any special notes about this client..."
                autoComplete="off"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingClient ? "Update" : "Add"} Client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Follow-ups Dialog */}
              <Dialog
          open={followupsDialogOpen}
          onClose={() => {
            setFollowupsDialogOpen(false);
            setFollowupsClient(null);
            setError("");
            setSuccessMessage("");
          }}
          maxWidth="md"
          fullWidth
        >
        <DialogTitle>
          <Typography variant="h6">
            Follow-ups for {followupsClient?.first_name || 'Unknown'} {followupsClient?.last_name || 'Client'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Error Message Display */}
            {error && (
              <Alert severity="error" sx={{ mb: 2, color: "error.main" }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            
            {/* Success Message Display */}
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
                {successMessage}
              </Alert>
            )}
            
            {/* Row 1: Follow-up Date + Time */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Follow-up Date"
                type="date"
                value={newFollowup.followup_date}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const today = new Date().toISOString().split('T')[0];
                  
                  // Only allow dates up to today
                  if (selectedDate <= today) {
                    setNewFollowup({ ...newFollowup, followup_date: selectedDate });
                  }
                }}
                required
                error={!!error && !newFollowup.followup_date}
                helperText={error && !newFollowup.followup_date ? 'Follow-up date is required' : 'Cannot be future date'}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: (() => {
                    const today = new Date();
                    const year = today.getFullYear();
                    const month = String(today.getMonth() + 1).padStart(2, '0');
                    const day = String(today.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  })()
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-error": {
                      borderColor: "error.main",
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={newFollowup.followup_time}
                onChange={(e) => setNewFollowup({ ...newFollowup, followup_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Next Follow-up Date"
                type="date"
                value={newFollowup.next_followup_date}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const today = new Date().toISOString().split('T')[0];
                  
                  // Only allow dates from today onwards
                  if (selectedDate >= today) {
                    setNewFollowup({ ...newFollowup, next_followup_date: selectedDate });
                  }
                }}
                helperText="Cannot be past date"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: new Date().toISOString().split('T')[0]
                }}
              />
              <TextField
                fullWidth
                label="Next Follow-up Time"
                type="time"
                value={newFollowup.next_followup_time}
                onChange={(e) => setNewFollowup({ ...newFollowup, next_followup_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Row 4: Notes (full width) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                value={newFollowup.notes}
                onChange={(e) => setNewFollowup({ ...newFollowup, notes: e.target.value })}
                multiline
                rows={3}
                placeholder="Add your comment here... (optional)"
              />
            </Box>
            {/* Row 3: Status (full width) */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                select
                label="Status"
                value={newFollowup.status}
                onChange={(e) => setNewFollowup({ ...newFollowup, status: e.target.value })}
                required
              >
                <MenuItem value="cold">Cold</MenuItem>
                <MenuItem value="warm">Warm</MenuItem>
                <MenuItem value="hot">Hot</MenuItem>
                <MenuItem value="wrong-number">Wrong-number</MenuItem>
                <MenuItem value="lost-to-competitor">Lost-to-competitor</MenuItem>
                <MenuItem value="future-contact">Future-Contact</MenuItem>
                <MenuItem value="lead-converted">Lead-Converted</MenuItem>
                <MenuItem value="not-interested">Not Interested</MenuItem>
                <MenuItem value="dnd">DND(Do not dial)</MenuItem>
              </TextField>
              <Button 
                fullWidth
            onClick={() => {
              handleCreateFollowup();
            }}
            variant="contained"
            disabled={creatingFollowup}
          >
            {creatingFollowup ? 'Adding...' : 'Add Followup'}
          </Button>
            </Box>

          </Box>
          
          {/* Followups List */}
          <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
            Follow-up History
          </Typography>
          
          {followupsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : clientFollowups.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Notifications sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No follow-ups found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This client doesn't have any follow-ups yet
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Total Follow-ups: {clientFollowups.length}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date Followed</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Next Follow-up</TableCell>
                      <TableCell>Comment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientFollowups.map((followup) => (
                      <TableRow key={followup.id}>
                        <TableCell>
                          {new Date(followup.followup_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {followup.followup_time ? followup.followup_time.substring(0, 5) : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={followup.status}
                            color={followup.status === 'hot' ? 'error' : 
                                   followup.status === 'warm' ? 'warning' : 
                                   followup.status === 'cold' ? 'info' : 'default'}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          {followup.next_followup_date ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {new Date(followup.next_followup_date).toLocaleDateString()}
                              </Typography>
                              {followup.next_followup_time && (
                                <Typography variant="caption" color="textSecondary">
                                  at {followup.next_followup_time.substring(0, 5)}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                              Not scheduled
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {followup.notes || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowupsDialogOpen(false)}>Cancel</Button>
          
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clients;
